/**
 * Help hub coverage registry.
 * Drives in-app guidance cards and the full in-app feature docs page.
 */
export const HELP_GUIDE_STATUS = {
  TODO: "todo",
  DRAFT: "draft",
  LIVE: "live",
  NEEDS_UPDATE: "needs-update",
};

export const HELP_SIDEBAR_GROUP_ORDER = [
  "Overview",
  "Operations",
  "Finance",
  "Workforce",
  "Assets",
  "Productivity",
  "Settings",
  "Account & Support",
];

/**
 * Every item represents one in-app documentation section.
 * Keep this aligned with routable features in App.jsx.
 */
const baseHelpCoverageRegistry = [
  {
    id: "dashboard",
    title: "Dashboard",
    description: "Track activity, counts, and operational health at a glance.",
    sidebarGroup: "Overview",
    featureKey: null,
    helpSectionId: "dashboard",
    inAppPath: "/",
    overview:
      "Dashboard is your command center for daily operations across trucking and service/repair workflows.",
    roles: ["owner", "admin", "dispatcher", "manager"],
    setupChecklist: [
      "Verify dashboard widgets align to your workflow priorities",
      "Review open jobs, invoice aging, and payout readiness every day",
      "Confirm feature modules shown match enabled account features",
    ],
    workflowSteps: [
      "Start the day by checking pending and overdue work",
      "Open priority modules from dashboard cards and breadcrumbs",
      "Return to dashboard for end-of-day completion check",
    ],
    troubleshooting: [
      {
        issue: "Missing metrics or empty cards",
        resolution:
          "Confirm related feature modules are enabled and data exists in the selected date window.",
      },
      {
        issue: "Counts do not match expected totals",
        resolution:
          "Validate status filters and check that jobs/invoices moved to the correct state.",
      },
    ],
    faq: [
      {
        question: "Who should monitor dashboard daily?",
        answer: "Owners, admins, and dispatch coordinators should review it at shift start.",
      },
    ],
    relatedFeatures: ["jobs", "dispatch", "reports", "invoices"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.LIVE,
    owner: "Operations",
    screenshotStatus: "partial",
  },
  {
    id: "jobs",
    title: "Jobs",
    description: "Create jobs, assign work, and track progress.",
    sidebarGroup: "Operations",
    featureKey: "jobs.enabled",
    helpSectionId: "jobs",
    inAppPath: "/jobs",
    overview:
      "Jobs are the primary work unit for scheduling, service execution, and downstream billing.",
    roles: ["owner", "admin", "dispatcher", "field lead"],
    setupChecklist: [
      "Define required job fields and team status standards",
      "Attach templates, notes, and required documents",
      "Ensure client/location data is complete before dispatch",
    ],
    workflowSteps: [
      "Create job and assign client/location context",
      "Set schedule, assignee, and operational notes",
      "Update statuses from open to completed",
      "Hand off completed jobs to invoicing/payout workflows",
    ],
    troubleshooting: [
      {
        issue: "Job cannot be completed",
        resolution:
          "Check required fields, missing attachments, or unresolved dependent tasks.",
      },
      {
        issue: "Assignments are not visible to team",
        resolution:
          "Verify user role permissions and confirm assignee account is active.",
      },
    ],
    faq: [
      {
        question: "Should every invoice originate from a job?",
        answer: "Yes for traceability. Link jobs whenever possible before invoicing.",
      },
    ],
    relatedFeatures: ["clients", "dispatch", "invoices", "payouts"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.LIVE,
    owner: "Operations",
    screenshotStatus: "partial",
  },
  {
    id: "dispatch",
    title: "Dispatch Board",
    description: "Coordinate schedule, assignments, and route execution.",
    sidebarGroup: "Operations",
    featureKey: "dispatch.enabled",
    helpSectionId: "dispatch",
    inAppPath: "/dispatch",
    overview:
      "Dispatch centralizes scheduling and assignment so teams can move from planned to completed work efficiently.",
    roles: ["owner", "admin", "dispatcher"],
    setupChecklist: [
      "Enable dispatch feature and confirm settings defaults",
      "Load active jobs and resources for the work window",
      "Define assignment conventions for trucking or service teams",
    ],
    workflowSteps: [
      "Review unassigned jobs",
      "Assign jobs to resources",
      "Monitor progress and resolve conflicts",
      "Close assignments after completion",
    ],
    troubleshooting: [
      {
        issue: "Jobs remain unassigned",
        resolution: "Confirm resource availability and required job constraints.",
      },
      {
        issue: "Drag/drop or scheduling appears blocked",
        resolution:
          "Refresh board filters and verify the selected date window and resource state.",
      },
    ],
    faq: [
      {
        question: "Can dispatch run without manifests?",
        answer: "Yes. Manifests are trucking-specific; dispatch supports broader scheduling.",
      },
    ],
    relatedFeatures: ["jobs", "drivers", "employees", "settings-dispatch"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.DRAFT,
    owner: "Dispatch",
    screenshotStatus: "pending",
  },
  {
    id: "clients",
    title: "Clients",
    description: "Manage customer profiles, locations, and relationships.",
    sidebarGroup: "Operations",
    featureKey: "clients.enabled",
    helpSectionId: "clients",
    inAppPath: "/clients",
    overview:
      "Clients and locations provide the operational and financial context for jobs, invoices, and communication.",
    roles: ["owner", "admin", "dispatcher", "account manager"],
    setupChecklist: [
      "Create complete client profile and billing details",
      "Add all service or destination locations",
      "Set communication preferences and account status",
    ],
    workflowSteps: [
      "Create client record",
      "Add locations and contacts",
      "Link client to jobs/invoices",
      "Review history before future scheduling",
    ],
    troubleshooting: [
      {
        issue: "Client not selectable in job/invoice forms",
        resolution: "Check client status and required billing/location data.",
      },
    ],
    faq: [
      {
        question: "Do locations need to be created before jobs?",
        answer: "Yes for accurate routing, dispatch context, and service tracking.",
      },
    ],
    relatedFeatures: ["jobs", "invoices", "reports"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.LIVE,
    owner: "Operations",
    screenshotStatus: "pending",
  },
  {
    id: "manifests",
    title: "Manifests (Trucking)",
    description: "Group loads and execute trucking workflows.",
    sidebarGroup: "Operations",
    featureKey: "manifests.enabled",
    appTypes: ["trucking"],
    helpSectionId: "manifests",
    inAppPath: "/manifests",
    overview:
      "Manifests package related loads/jobs for route-level execution and visibility in trucking operations.",
    roles: ["owner", "admin", "dispatcher"],
    setupChecklist: [
      "Verify manifest feature is enabled",
      "Collect jobs/loads that should ship together",
      "Assign responsible driver/team and validate route readiness",
    ],
    workflowSteps: [
      "Create manifest",
      "Attach jobs/loads",
      "Review assignments and status",
      "Close manifest on completion",
    ],
    troubleshooting: [
      {
        issue: "Manifest cannot be finalized",
        resolution: "Check for missing assignments, incomplete jobs, or invalid statuses.",
      },
    ],
    faq: [
      {
        question: "When should we use manifests instead of standalone jobs?",
        answer: "Use manifests when loads must be managed together operationally.",
      },
    ],
    relatedFeatures: ["jobs", "drivers", "dispatch", "payouts"],
    docsPluginId: "customer",
    docsSlug: "industry-guides/overview",
    guideStatus: HELP_GUIDE_STATUS.DRAFT,
    owner: "Trucking Ops",
    screenshotStatus: "pending",
  },
  {
    id: "calendar",
    title: "Calendar",
    description: "View scheduling windows and availability timeline.",
    sidebarGroup: "Operations",
    featureKey: null,
    helpSectionId: "calendar",
    inAppPath: "/calendar",
    overview:
      "Calendar provides a time-based view for planned work and schedule coordination.",
    roles: ["owner", "admin", "dispatcher"],
    setupChecklist: [
      "Confirm timezone and date preferences",
      "Ensure jobs/resources include valid schedule fields",
      "Agree on team cadence for calendar review",
    ],
    workflowSteps: [
      "Open target date range",
      "Review upcoming work and conflicts",
      "Adjust assignment timing as needed",
    ],
    troubleshooting: [
      {
        issue: "Schedule appears empty",
        resolution: "Verify filters and confirm jobs are assigned within selected dates.",
      },
    ],
    faq: [
      {
        question: "Should dispatch and calendar both be used?",
        answer: "Yes. Calendar helps with time planning, dispatch handles assignment operations.",
      },
    ],
    relatedFeatures: ["jobs", "dispatch"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.DRAFT,
    owner: "Operations",
    screenshotStatus: "pending",
  },
  {
    id: "invoices",
    title: "Invoices",
    description: "Generate invoices and track payment lifecycle.",
    sidebarGroup: "Finance",
    featureKey: "invoices.enabled",
    helpSectionId: "invoices",
    inAppPath: "/invoices",
    overview:
      "Invoices transform completed operational work into billable revenue with status tracking.",
    roles: ["owner", "admin", "finance"],
    setupChecklist: [
      "Confirm billing defaults and invoice templates",
      "Validate client and job links for each invoice",
      "Set payment terms and internal review checklist",
    ],
    workflowSteps: [
      "Create invoice from job/client context",
      "Review line items and totals",
      "Move invoice through status lifecycle",
      "Track payment completion and aging",
    ],
    troubleshooting: [
      {
        issue: "Invoice totals are incorrect",
        resolution: "Review selected jobs, rates, and manual line item edits.",
      },
    ],
    faq: [
      {
        question: "Can invoices be created before jobs complete?",
        answer: "Prefer completed jobs for accurate final amounts and auditability.",
      },
    ],
    relatedFeatures: ["clients", "jobs", "reports", "settings-billing"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.LIVE,
    owner: "Finance",
    screenshotStatus: "pending",
  },
  {
    id: "payouts",
    title: "Payouts",
    description: "Track payout records and compensation completion.",
    sidebarGroup: "Finance",
    featureKey: "payouts.enabled",
    appTypes: ["trucking", "serviceRepair"],
    helpSectionId: "payouts",
    inAppPath: "/payouts",
    overview:
      "Payouts provide visibility and control for post-work compensation and reconciliation.",
    roles: ["owner", "admin", "finance"],
    setupChecklist: [
      "Define payout policies and approval expectations",
      "Map payout records to jobs/manifests",
      "Confirm reconciliation cadence",
    ],
    workflowSteps: [
      "Create payout record",
      "Review related work completion",
      "Update payout statuses and settlement notes",
      "Close and reconcile totals",
    ],
    troubleshooting: [
      {
        issue: "Payout cannot move to completed",
        resolution: "Validate required fields, related job state, and approval sequence.",
      },
    ],
    faq: [
      {
        question: "Should payouts be tracked even for internal teams?",
        answer: "Yes, when compensation traceability or reconciliation is required.",
      },
    ],
    relatedFeatures: ["jobs", "manifests", "reports"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.DRAFT,
    owner: "Finance Ops",
    screenshotStatus: "pending",
  },
  {
    id: "reports",
    title: "Reports",
    description: "Analyze operations and financial outcomes.",
    sidebarGroup: "Finance",
    featureKey: "reports.enabled",
    appTypes: ["trucking", "serviceRepair"],
    helpSectionId: "reports",
    inAppPath: "/reports",
    overview:
      "Reports aggregate performance and financial data for planning, review, and leadership decisions.",
    roles: ["owner", "admin", "manager", "finance"],
    setupChecklist: [
      "Confirm source modules contain clean status data",
      "Align report date ranges and review cadence",
      "Share key KPI definitions across leadership",
    ],
    workflowSteps: [
      "Select report type and date range",
      "Apply filters to isolate target segment",
      "Review trends and export/share outputs",
    ],
    troubleshooting: [
      {
        issue: "Reports show unexpected gaps",
        resolution: "Audit underlying module statuses and date filters.",
      },
    ],
    faq: [
      {
        question: "Which modules drive report quality most?",
        answer: "Jobs, invoices, payouts, and client data consistency.",
      },
    ],
    relatedFeatures: ["dashboard", "jobs", "invoices", "payouts", "clients"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.DRAFT,
    owner: "Leadership",
    screenshotStatus: "pending",
  },
  {
    id: "users",
    title: "Users",
    description: "Manage portal user access and roles.",
    sidebarGroup: "Workforce",
    featureKey: "settings.users.enabled",
    helpSectionId: "users",
    inAppPath: "/users",
    overview:
      "Users controls access to portal capabilities through role-based permissions.",
    roles: ["owner", "admin"],
    setupChecklist: [
      "Define role matrix for your operation",
      "Create user accounts with correct authority",
      "Verify notification and contact fields",
    ],
    workflowSteps: [
      "Create user",
      "Assign authority",
      "Review account access and adjust over time",
    ],
    troubleshooting: [
      {
        issue: "User cannot access expected pages",
        resolution:
          "Validate authority role and feature flags for modules they need.",
      },
    ],
    faq: [
      {
        question: "Should we create separate roles for dispatch and finance?",
        answer: "Yes. Keep least-privilege access aligned to team responsibilities.",
      },
    ],
    relatedFeatures: ["employees", "settings-users", "account-settings"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.LIVE,
    owner: "Admin Experience",
    screenshotStatus: "pending",
  },
  {
    id: "employees",
    title: "Employees",
    description: "Track employee records and operational assignment data.",
    sidebarGroup: "Workforce",
    featureKey: "employees.enabled",
    appTypes: ["serviceRepair"],
    helpSectionId: "employees",
    inAppPath: "/employees",
    overview:
      "Employees stores workforce profile data used for scheduling and execution.",
    roles: ["owner", "admin", "manager"],
    setupChecklist: [
      "Create core employee profiles",
      "Capture assignment-relevant details",
      "Keep status and availability up to date",
    ],
    workflowSteps: [
      "Create employee profile",
      "Associate with jobs/dispatch flows",
      "Maintain profile accuracy over time",
    ],
    troubleshooting: [
      {
        issue: "Employee does not appear in assignment flows",
        resolution:
          "Verify employee is active and required profile data is complete.",
      },
    ],
    faq: [
      {
        question: "How are employees different from users?",
        answer: "Employees track workforce data; users control portal login access.",
      },
    ],
    relatedFeatures: ["users", "dispatch", "jobs"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.DRAFT,
    owner: "Workforce Ops",
    screenshotStatus: "pending",
  },
  {
    id: "drivers",
    title: "Drivers",
    description: "Manage driver records for trucking operations.",
    sidebarGroup: "Workforce",
    featureKey: "drivers.enabled",
    appTypes: ["trucking"],
    helpSectionId: "drivers",
    inAppPath: "/drivers",
    overview:
      "Drivers is the trucking-specific workforce module used in manifests and dispatch flows.",
    roles: ["owner", "admin", "dispatcher"],
    setupChecklist: [
      "Create driver profiles and status",
      "Capture route and assignment information",
      "Validate readiness before manifest assignment",
    ],
    workflowSteps: [
      "Create/update driver profile",
      "Assign drivers to manifests/jobs",
      "Monitor execution and update statuses",
    ],
    troubleshooting: [
      {
        issue: "Driver unavailable for assignment",
        resolution:
          "Check profile status, route constraints, and dispatch filters.",
      },
    ],
    faq: [
      {
        question: "Is Drivers required for non-trucking app types?",
        answer: "No. It is primarily used for trucking workflows.",
      },
    ],
    relatedFeatures: ["manifests", "dispatch", "jobs"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.DRAFT,
    owner: "Trucking Ops",
    screenshotStatus: "pending",
  },
  {
    id: "equipment",
    title: "Equipment",
    description: "Track service equipment and related records.",
    sidebarGroup: "Assets",
    featureKey: null,
    helpSectionId: "equipment",
    inAppPath: "/equipment",
    overview:
      "Equipment tracks physical assets used in service/repair operations.",
    roles: ["owner", "admin", "manager"],
    setupChecklist: [
      "Create equipment records with clear identifiers",
      "Assign equipment ownership and status",
      "Link equipment to relevant job workflows",
    ],
    workflowSteps: [
      "Register equipment",
      "Associate with operational work as needed",
      "Maintain lifecycle and status updates",
    ],
    troubleshooting: [
      {
        issue: "Equipment record not available in workflows",
        resolution: "Verify profile completeness and account access permissions.",
      },
    ],
    faq: [
      {
        question: "Should equipment be tracked even for small teams?",
        answer: "Yes when asset utilization affects scheduling and profitability.",
      },
    ],
    relatedFeatures: ["inventory", "jobs", "reports"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.DRAFT,
    owner: "Service Ops",
    screenshotStatus: "pending",
  },
  {
    id: "inventory",
    title: "Inventory",
    description: "Manage inventory items used in operations.",
    sidebarGroup: "Assets",
    featureKey: "inventory.enabled",
    appTypes: ["serviceRepair"],
    helpSectionId: "inventory",
    inAppPath: "/inventory",
    overview:
      "Inventory helps teams track stock usage and reduce delays from missing materials.",
    roles: ["owner", "admin", "manager"],
    setupChecklist: [
      "Create inventory catalog and item structure",
      "Set stock thresholds and review cadence",
      "Align inventory usage with job workflows",
    ],
    workflowSteps: [
      "Add inventory items",
      "Track allocation/consumption",
      "Review stock and replenish proactively",
    ],
    troubleshooting: [
      {
        issue: "Inventory quantities drift from expected",
        resolution:
          "Review consumption logs and ensure update process is followed consistently.",
      },
    ],
    faq: [
      {
        question: "Is inventory needed for trucking-only teams?",
        answer: "Usually not, unless parts/material tracking is operationally required.",
      },
    ],
    relatedFeatures: ["equipment", "jobs", "reports"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.DRAFT,
    owner: "Service Ops",
    screenshotStatus: "pending",
  },
  {
    id: "feedback",
    title: "Feedback & Support",
    description: "Create support tickets and report issues.",
    sidebarGroup: "Productivity",
    featureKey: null,
    helpSectionId: "feedback",
    inAppPath: "/feedback/create",
    overview:
      "Feedback allows teams to submit bugs, requests, and support needs directly from the app.",
    roles: ["all users"],
    setupChecklist: [
      "Define internal issue-reporting expectations",
      "Capture reproducible details when reporting bugs",
      "Use consistent severity and impact language",
    ],
    workflowSteps: [
      "Open feedback form",
      "Describe issue or request",
      "Submit and track response updates",
    ],
    troubleshooting: [
      {
        issue: "Support cannot reproduce issue",
        resolution:
          "Include route, role, feature state, and exact steps with expected/actual behavior.",
      },
    ],
    faq: [
      {
        question: "Where should walkthrough/doc corrections be requested?",
        answer: "Submit them via feedback with impacted feature section and screenshots.",
      },
    ],
    relatedFeatures: ["dashboard", "account-settings"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.LIVE,
    owner: "Support",
    screenshotStatus: "pending",
  },
  {
    id: "settings-general",
    title: "Settings: General",
    description: "Configure base account preferences and defaults.",
    sidebarGroup: "Settings",
    featureKey: "settings.enabled",
    helpSectionId: "settings-general",
    inAppPath: "/settings/general",
    overview:
      "General settings control base account behavior used across all modules.",
    roles: ["owner", "admin"],
    setupChecklist: [
      "Complete account profile and operational defaults",
      "Validate locale/timezone/business identity fields",
      "Confirm downstream modules inherit expected defaults",
    ],
    workflowSteps: [
      "Review general settings values",
      "Update and save changes",
      "Verify effects in jobs/invoices/dispatch flows",
    ],
    troubleshooting: [
      {
        issue: "Changes are not reflected in modules",
        resolution: "Refresh affected page and verify module-level overrides are not in place.",
      },
    ],
    faq: [
      {
        question: "Who should manage global settings?",
        answer: "Owners and admins should own governance for account-wide defaults.",
      },
    ],
    relatedFeatures: ["settings-rates", "settings-templates", "settings-users"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.LIVE,
    owner: "Admin Experience",
    screenshotStatus: "pending",
  },
  {
    id: "settings-rates",
    title: "Settings: Rates",
    description: "Maintain rate configuration used in billing workflows.",
    sidebarGroup: "Settings",
    featureKey: "settings.rates.enabled",
    helpSectionId: "settings-rates",
    inAppPath: "/settings/rates",
    overview:
      "Rates determine pricing behavior and should be reviewed before invoice generation.",
    roles: ["owner", "admin", "finance"],
    setupChecklist: [
      "Review rate tables and current pricing policy",
      "Confirm rate applicability across job types",
      "Validate impact on invoice totals",
    ],
    workflowSteps: [
      "Open rates settings",
      "Adjust rate entries",
      "Confirm invoice previews reflect updates",
    ],
    troubleshooting: [
      {
        issue: "Invoice amounts do not match expected rates",
        resolution: "Check rate effective values and conflicting manual overrides.",
      },
    ],
    faq: [
      {
        question: "How often should rates be reviewed?",
        answer: "At least monthly or when operational costs change materially.",
      },
    ],
    relatedFeatures: ["invoices", "settings-billing"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.DRAFT,
    owner: "Finance",
    screenshotStatus: "pending",
  },
  {
    id: "settings-users",
    title: "Settings: Users",
    description: "Configure user-management policies inside settings.",
    sidebarGroup: "Settings",
    featureKey: "settings.users.enabled",
    helpSectionId: "settings-users",
    inAppPath: "/settings/users",
    overview:
      "Settings users page complements core user management with policy and access configuration.",
    roles: ["owner", "admin"],
    setupChecklist: [
      "Review active user access patterns",
      "Confirm role policies and permission boundaries",
      "Apply updates with least-privilege defaults",
    ],
    workflowSteps: [
      "Open settings users",
      "Review accounts and policies",
      "Adjust access as roles evolve",
    ],
    troubleshooting: [
      {
        issue: "Role updates do not apply immediately",
        resolution: "Have user refresh session and verify authority + feature flags.",
      },
    ],
    faq: [
      {
        question: "Should we manage users from settings or users module?",
        answer: "Both are valid; follow your team’s governance workflow.",
      },
    ],
    relatedFeatures: ["users", "account-settings"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.DRAFT,
    owner: "Admin Experience",
    screenshotStatus: "pending",
  },
  {
    id: "settings-billing",
    title: "Settings: Billing",
    description: "Manage billing configuration and defaults.",
    sidebarGroup: "Settings",
    featureKey: "settings.enabled",
    helpSectionId: "settings-billing",
    inAppPath: "/settings/billing",
    overview:
      "Billing settings define account-level billing behavior used by invoices and finance workflows.",
    roles: ["owner", "admin", "finance"],
    setupChecklist: [
      "Review billing profile and required fields",
      "Align billing configuration with accounting process",
      "Validate downstream invoice workflows",
    ],
    workflowSteps: [
      "Open billing settings",
      "Update billing defaults",
      "Verify invoice creation uses updated values",
    ],
    troubleshooting: [
      {
        issue: "Billing defaults not appearing on new invoices",
        resolution: "Check for invoice-level overrides and ensure save completed.",
      },
    ],
    faq: [
      {
        question: "Who should own billing settings?",
        answer: "Finance lead with owner/admin oversight.",
      },
    ],
    relatedFeatures: ["invoices", "settings-rates"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.DRAFT,
    owner: "Finance",
    screenshotStatus: "pending",
  },
  {
    id: "settings-features",
    title: "Settings: Features",
    description: "Review and manage enabled capabilities.",
    sidebarGroup: "Settings",
    featureKey: "settings.enabled",
    helpSectionId: "settings-features",
    inAppPath: "/settings/features",
    overview:
      "Features settings governs which modules are available in the account experience.",
    roles: ["owner", "admin"],
    setupChecklist: [
      "Review enabled feature set for your operation",
      "Validate role impact before enabling/disabling modules",
      "Communicate changes to affected teams",
    ],
    workflowSteps: [
      "Open features settings",
      "Adjust enabled modules",
      "Re-check navigation and workflows",
    ],
    troubleshooting: [
      {
        issue: "Menu items disappear unexpectedly",
        resolution: "Inspect feature enablement and account-level config sync.",
      },
    ],
    faq: [
      {
        question: "Can feature toggles affect existing records?",
        answer: "They affect access/visibility; existing data is typically retained.",
      },
    ],
    relatedFeatures: ["dashboard", "settings-general"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.DRAFT,
    owner: "Admin Experience",
    screenshotStatus: "pending",
  },
  {
    id: "settings-templates",
    title: "Settings: Templates",
    description: "Configure reusable templates for repeatable workflows.",
    sidebarGroup: "Settings",
    featureKey: "settings.templates.enabled",
    helpSectionId: "settings-templates",
    inAppPath: "/settings/templates",
    overview:
      "Templates speed up repeated work patterns and improve consistency across teams.",
    roles: ["owner", "admin", "manager"],
    setupChecklist: [
      "Define template naming conventions",
      "Create baseline templates for high-volume workflows",
      "Review template usage with teams",
    ],
    workflowSteps: [
      "Create template",
      "Apply template in operational flow",
      "Refine template based on outcomes",
    ],
    troubleshooting: [
      {
        issue: "Template not available where expected",
        resolution: "Validate template scope and feature enablement.",
      },
    ],
    faq: [
      {
        question: "Should templates be role-restricted?",
        answer: "Yes where uncontrolled edits could impact operational consistency.",
      },
    ],
    relatedFeatures: ["jobs", "invoices", "settings-notification-templates"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.DRAFT,
    owner: "Operations",
    screenshotStatus: "pending",
  },
  {
    id: "settings-task-templates",
    title: "Settings: Task Templates",
    description: "Configure reusable task structures where available.",
    sidebarGroup: "Settings",
    featureKey: "settings.taskTemplates.enabled",
    helpSectionId: "settings-task-templates",
    inAppPath: "/settings/task-templates",
    overview:
      "Task templates standardize repeated task sequences and handoffs.",
    roles: ["owner", "admin", "manager"],
    setupChecklist: [
      "Define canonical task sequences",
      "Align naming and completion rules",
      "Pilot templates with core team before broad rollout",
    ],
    workflowSteps: [
      "Create task template",
      "Attach template in target workflow",
      "Review completion quality and refine",
    ],
    troubleshooting: [
      {
        issue: "Task template is unavailable",
        resolution: "Verify feature flag and backend readiness for the module.",
      },
    ],
    faq: [
      {
        question: "Is this module always enabled?",
        answer: "No, availability depends on feature readiness and account config.",
      },
    ],
    relatedFeatures: ["settings-templates", "jobs"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.TODO,
    owner: "Operations",
    screenshotStatus: "pending",
  },
  {
    id: "settings-notification-templates",
    title: "Settings: Notification Templates",
    description: "Manage reusable communication templates.",
    sidebarGroup: "Settings",
    featureKey: "settings.notificationTemplates.enabled",
    helpSectionId: "settings-notification-templates",
    inAppPath: "/settings/notification-templates",
    overview:
      "Notification templates ensure communication consistency across workflows.",
    roles: ["owner", "admin", "manager"],
    setupChecklist: [
      "Create baseline notification templates",
      "Validate wording and placeholders",
      "Align sending rules with team process",
    ],
    workflowSteps: [
      "Create/update template",
      "Apply in related workflow",
      "Review delivery and quality",
    ],
    troubleshooting: [
      {
        issue: "Template content appears incorrect",
        resolution: "Check placeholders and latest saved version.",
      },
    ],
    faq: [
      {
        question: "Should template edits be audited?",
        answer: "Yes, especially for customer-facing operational notifications.",
      },
    ],
    relatedFeatures: ["settings-templates", "settings-email"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.DRAFT,
    owner: "Communications",
    screenshotStatus: "pending",
  },
  {
    id: "settings-reports",
    title: "Settings: Reports",
    description: "Configure report settings and output behavior.",
    sidebarGroup: "Settings",
    featureKey: "settings.reports.enabled",
    helpSectionId: "settings-reports",
    inAppPath: "/settings/reports",
    overview:
      "Report settings let teams tailor how reporting outputs are generated and shared.",
    roles: ["owner", "admin", "manager", "finance"],
    setupChecklist: [
      "Review current report output defaults",
      "Align report settings with leadership review process",
      "Validate permissions for report consumers",
    ],
    workflowSteps: [
      "Open report settings",
      "Adjust output and defaults",
      "Validate reports downstream",
    ],
    troubleshooting: [
      {
        issue: "Report output format is unexpected",
        resolution: "Check settings overrides and route-level filters.",
      },
    ],
    faq: [
      {
        question: "Do report settings affect historical data?",
        answer: "They usually affect output behavior, not historical source records.",
      },
    ],
    relatedFeatures: ["reports", "dashboard"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.DRAFT,
    owner: "Leadership",
    screenshotStatus: "pending",
  },
  {
    id: "settings-email",
    title: "Settings: Email",
    description: "Manage email configuration used by communication flows.",
    sidebarGroup: "Settings",
    featureKey: "settings.enabled",
    helpSectionId: "settings-email",
    inAppPath: "/settings/email",
    overview:
      "Email settings controls communication-level configuration used in customer and team notifications.",
    roles: ["owner", "admin"],
    setupChecklist: [
      "Verify sender and domain-related settings",
      "Review template compatibility with email settings",
      "Test sample notification flows",
    ],
    workflowSteps: [
      "Open email settings",
      "Update configuration values",
      "Validate notification outputs",
    ],
    troubleshooting: [
      {
        issue: "Email templates are not delivering as expected",
        resolution: "Check email settings and notification template configuration.",
      },
    ],
    faq: [
      {
        question: "Should email settings be changed frequently?",
        answer: "No, only when communication policy or infrastructure changes.",
      },
    ],
    relatedFeatures: ["settings-notification-templates"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.DRAFT,
    owner: "Communications",
    screenshotStatus: "pending",
  },
  {
    id: "settings-dispatch",
    title: "Settings: Dispatch",
    description: "Configure dispatch-specific behavior and defaults.",
    sidebarGroup: "Settings",
    featureKey: "settings.dispatch.enabled",
    helpSectionId: "settings-dispatch",
    inAppPath: "/settings/dispatch",
    overview:
      "Dispatch settings controls scheduling and assignment defaults for operational planning.",
    roles: ["owner", "admin", "dispatcher"],
    setupChecklist: [
      "Review dispatch default configuration",
      "Align dispatch settings with team scheduling process",
      "Validate with live dispatch workflows",
    ],
    workflowSteps: [
      "Open dispatch settings",
      "Apply desired defaults",
      "Test in dispatch board operations",
    ],
    troubleshooting: [
      {
        issue: "Dispatch board does not reflect expected defaults",
        resolution: "Confirm settings save completed and refresh dispatch view.",
      },
    ],
    faq: [
      {
        question: "Do dispatch settings apply immediately?",
        answer: "Typically yes, but active sessions may need refresh.",
      },
    ],
    relatedFeatures: ["dispatch", "jobs"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.DRAFT,
    owner: "Dispatch",
    screenshotStatus: "pending",
  },
  {
    id: "settings-advanced",
    title: "Settings: Advanced",
    description: "Manage advanced account and workflow options.",
    sidebarGroup: "Settings",
    featureKey: "settings.advanced.enabled",
    helpSectionId: "settings-advanced",
    inAppPath: "/settings/advanced",
    overview:
      "Advanced settings contains sensitive or high-impact configuration for experienced administrators.",
    roles: ["owner", "admin"],
    setupChecklist: [
      "Review change impact before editing advanced settings",
      "Coordinate updates with affected teams",
      "Document changes for auditability",
    ],
    workflowSteps: [
      "Open advanced settings",
      "Apply controlled changes",
      "Validate operational behavior post-update",
    ],
    troubleshooting: [
      {
        issue: "Unexpected behavior after advanced changes",
        resolution:
          "Re-check changed values and roll back to known-good configuration where needed.",
      },
    ],
    faq: [
      {
        question: "Who can edit advanced settings?",
        answer: "Only owner/admin users with governance responsibility.",
      },
    ],
    relatedFeatures: ["settings-general", "settings-features"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.TODO,
    owner: "Admin Experience",
    screenshotStatus: "pending",
  },
  {
    id: "account-settings",
    title: "Account Settings",
    description: "Manage account profile and notification preferences.",
    sidebarGroup: "Account & Support",
    featureKey: null,
    helpSectionId: "account-settings",
    inAppPath: "/account/information",
    overview:
      "Account settings allows each user to manage personal profile and account preferences.",
    roles: ["all users"],
    setupChecklist: [
      "Complete profile information",
      "Review account settings and preferences",
      "Set notification choices for operational visibility",
    ],
    workflowSteps: [
      "Open account settings",
      "Update profile or notifications",
      "Save and verify changes",
    ],
    troubleshooting: [
      {
        issue: "Notification preferences do not seem applied",
        resolution:
          "Reopen notification settings and verify all toggles are saved successfully.",
      },
    ],
    faq: [
      {
        question: "Can users edit only their own account settings?",
        answer: "Yes for personal settings; admin-level changes are handled in user modules.",
      },
    ],
    relatedFeatures: ["users", "settings-users", "feedback"],
    docsPluginId: "customer",
    docsSlug: "intro",
    guideStatus: HELP_GUIDE_STATUS.LIVE,
    owner: "Admin Experience",
    screenshotStatus: "pending",
  },
];

const getDefaultDecisionPoints = (section) => {
  const points = [];
  if (section.featureKey) {
    points.push(
      "If the module is not visible, confirm feature enablement in account feature settings."
    );
  }
  if (Array.isArray(section.roles) && section.roles.length) {
    points.push(
      `If access is blocked, confirm your role is one of: ${section.roles.join(", ")}.`
    );
  }
  if (String(section.id).startsWith("settings-")) {
    points.push(
      "When changing settings, save and refresh related pages to confirm the new defaults are applied."
    );
  }
  return points;
};

const getDefaultValidationChecks = (section) => {
  const checks = [
    "The section workflow completes without validation errors.",
    "Updated records are visible after refresh/navigation.",
  ];

  if (section.id === "jobs") {
    checks.push("New job appears in the jobs list with the correct status.");
  } else if (section.id === "dispatch") {
    checks.push("Assignment changes are reflected on the dispatch board.");
  } else if (section.id === "invoices") {
    checks.push("Invoice totals and status updates match expected values.");
  } else if (String(section.id).startsWith("settings-")) {
    checks.push("New settings values are reflected in downstream module behavior.");
  }

  return checks;
};

const withTutorialFields = (section) => {
  return {
    ...section,
    prerequisites: section.prerequisites || section.setupChecklist || [],
    stepByStep: section.stepByStep || section.workflowSteps || section.firstActions || [],
    decisionPoints: section.decisionPoints || getDefaultDecisionPoints(section),
    validationChecks: section.validationChecks || getDefaultValidationChecks(section),
  };
};

export const helpCoverageRegistry = baseHelpCoverageRegistry.map(withTutorialFields);

/**
 * Structured contract for attunelogic-docs integration:
 * in-app feature entry -> docs plugin -> docs slug.
 */
export const docsLinkContract = helpCoverageRegistry.map((item) => ({
  featureId: item.id,
  featureKey: item.featureKey,
  helpSectionId: item.helpSectionId,
  docsPluginId: item.docsPluginId,
  docsSlug: item.docsSlug,
  inAppPath: item.inAppPath,
}));

export default helpCoverageRegistry;
